import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import type { User } from '@recruitee/account-types';
import { I18nService } from '@recruitee/i18n';
import type { SelectOption } from '@recruitee/user-interface';
import { AvatarComponent, SelectComponent } from '@recruitee/user-interface';

import { Account } from '../../../client/integrations-client.types';
import { CoreApiService, Destroyable, takeUntilDestroyed } from '../../../common';
import type { DynamicAdminSelectInputConfig } from '../../types';
import { DynamicFormInput } from '../dynamic-form/dynamic-form-input/dynamic-form-input';

export class AdminSelectInput extends DynamicFormInput<DynamicAdminSelectInputConfig> {
  public get hasSearch(): boolean {
    return !!this.config.hasSearch;
  }

  public get includeEmptyOption(): boolean {
    return !this.config.required;
  }

  public isHidden(): boolean {
    return !this.config.required && !!this.config.hideIfEmpty;
  }
}

@Component({
  selector: 'rti-admin-select-input',
  templateUrl: './admin-select-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, AvatarComponent, SelectComponent],
})
@Destroyable()
export class AdminSelectInputComponent {
  @Input() public input: AdminSelectInput;

  public isLoading: boolean;
  public currentAdminId: number;
  public resourceSelectOptions: Array<SelectOption<number>>;

  constructor(
    private coreApi: CoreApiService,
    private cdRef: ChangeDetectorRef,
    private i18n: I18nService,
    private account: Account,
  ) {}

  public ngOnInit(): void {
    this.currentAdminId = this.input.config.value || this.account.admin.id;
    this.isLoading = true;
    this.cdRef.detectChanges();

    this.coreApi
      .get('/admins')
      .pipe(takeUntilDestroyed(this))
      .subscribe((data: { admins: User[] }) => {
        this.resourceSelectOptions = data.admins.map(admin => ({
          ...admin,
          value: admin.id,
          label: `${admin.firstName} ${admin.lastName}`,
        }));

        this.maybePreselectInputValue();

        this.isLoading = false;

        this.cdRef.detectChanges();
      });
  }

  public get selectPlaceholder(): string | undefined {
    return this.isLoading ? this.i18n.translate('rti.common.loading') : undefined;
  }

  private maybePreselectInputValue(): void {
    const initialOption = this.resourceSelectOptions.find(
      option => option.value === this.currentAdminId,
    );

    if (!initialOption) return;

    this.input.control.setValue(initialOption.value, { emitEvent: false });
  }
}
